import { memo, useCallback, useRef, useState, useEffect } from 'react'

import { Icon } from '@clientbase/clientbase-library'
import { alpha } from '@mui/material'
import { useRouter } from 'next/router'

import MerchantBadge from 'components/MerchantBadge'

import { theme } from 'styles/theme'

import { navigateFromNotification } from '../../navigateFromNotification'
import * as S from './NotificationItem.styles'

const ICON_MAP: Record<string, string> = {
  paid: 'paid',
  warning: 'notificationImportant',
  taskAlt: 'checkCircle',
  checkCircle: 'checkCircle',
  eventBusy: 'event',
  personAdd: 'person',
  error: 'error',
  accountBalance: 'accountBalance',
  autorenew: 'cached',
  tune: 'settings',
  creditCard: 'faCreditCard',
  assignment: 'assignment',
  uploadFile: 'uploadFile',
  notifications: 'faBell',
  accountTree: 'storefront',
  receipt: 'receipt',
  description: 'description',
  person: 'person',
  settings: 'settings'
}

const TYPE_COLORS: Record<string, string> = {
  billing: theme.palette.primary[100],
  contract: theme.palette.info[300],
  customer: theme.palette.success[300],
  nfse: theme.palette.secondary[100],
  system: theme.palette.alert[300],
  alert: theme.palette.error[300],
  workflow: theme.palette.secondary[100],
  general: theme.palette.neutral[400],
  accounts_payable: theme.palette.secondary[200]
}

const TYPE_LABELS: Record<string, string> = {
  general: 'Geral',
  workflow: 'Fluxo',
  billing: 'Cobrança',
  contract: 'Contrato',
  customer: 'Cliente',
  nfse: 'Nota Fiscal',
  system: 'Sistema',
  alert: 'Alerta',
  accounts_payable: 'Conta a pagar'
}

interface NotificationItemProps {
  uuid: string
  title: string
  body: string | null
  notification_type: string
  read: boolean
  icon?: string
  navigation_url?: string | null
  event_type?: string
  created_at: string
  // SC-10999: empresa da notificação, só na visão multi-empresas
  merchant?: { uuid?: string; name: string; logo_url?: string | null } | null
  onMarkAsRead: (uuid: string) => void
  onOpenBillingDetail?: (billingUuid: string) => void
  onOpenNfseDetail?: (nfseUuid: string) => void
  onOpenReportModal?: () => void
  onOpenBalanceModal?: (tab: string, disputeUuid?: string) => void
  onCloseDrawer?: () => void
}

const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSeconds < 60) return 'Agora'
  if (diffMinutes < 60) return `${diffMinutes}min`
  if (diffHours < 24) return `${diffHours}h`
  if (diffDays < 7) return `${diffDays}d`
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
}

const NotificationItem = ({
  uuid,
  title,
  body,
  notification_type,
  read,
  icon,
  navigation_url,
  event_type,
  created_at,
  merchant,
  onMarkAsRead,
  onOpenBillingDetail,
  onOpenNfseDetail,
  onOpenReportModal,
  onOpenBalanceModal,
  onCloseDrawer
}: NotificationItemProps) => {
  const router = useRouter()

  const handleClick = () => {
    if (!read) {
      onMarkAsRead(uuid)
    }

    navigateFromNotification(
      { navigation_url, event_type },
      {
        router,
        onOpenBillingDetail,
        onOpenNfseDetail,
        onOpenReportModal,
        onOpenBalanceModal,
        onCloseDrawer
      }
    )
  }

  const [expanded, setExpanded] = useState(false)
  const [isTruncated, setIsTruncated] = useState(false)
  const bodyRef = useRef<HTMLParagraphElement>(null)

  useEffect(() => {
    const el = bodyRef.current
    if (el) {
      setIsTruncated(el.scrollHeight > el.clientHeight)
    }
  }, [body])

  const handleToggleExpand = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setExpanded((prev) => !prev)
  }, [])

  const mappedIcon = icon && ICON_MAP[icon] ? ICON_MAP[icon] : 'faBell'
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const iconName = mappedIcon as any
  const typeColor = TYPE_COLORS[notification_type] || theme.palette.neutral[400]
  const typeLabel = TYPE_LABELS[notification_type] || notification_type

  return (
    <S.NotificationWrapper isUnread={!read} onClick={handleClick}>
      <S.IconWrapper bgColor={alpha(typeColor, 0.1)}>
        <Icon icon={iconName} colorSX={typeColor} width="16px" height="16px" />
      </S.IconWrapper>

      <S.ContentWrapper>
        <S.TopRow>
          <S.Title>{title}</S.Title>
          <S.Time>{formatRelativeTime(created_at)}</S.Time>
        </S.TopRow>
        {body && (
          <S.Body ref={bodyRef} expanded={expanded}>
            {body}
          </S.Body>
        )}
        <S.MetaRow>
          <S.TypeBadge>{typeLabel}</S.TypeBadge>
          {merchant && (
            <MerchantBadge
              name={merchant.name}
              logoUrl={merchant.logo_url}
              size={16}
              variant="smLight"
            />
          )}
          {!read && <S.UnreadDot />}
          {isTruncated && (
            <S.ExpandToggle onClick={handleToggleExpand} expanded={expanded}>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </S.ExpandToggle>
          )}
        </S.MetaRow>
      </S.ContentWrapper>
    </S.NotificationWrapper>
  )
}

export default memo(NotificationItem)
